
import React, { useState, useEffect } from 'react';
import { Promo } from '../types';
import { StorageService } from '../services/storageService';
import { X, ExternalLink, Copy, Check, Sparkles, Zap, Tag } from 'lucide-react';
import { useAuth } from './AuthBridge';

export const PromoBanner: React.FC = () => {
    const { user } = useAuth();
    const [promo, setPromo] = useState<Promo | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        const checkPromo = async () => {
            if (user?.planTier === 'paid') return;
            
            const lastDismissed = localStorage.getItem('dismissed_promo_time');
            if (lastDismissed) {
                const now = Date.now();
                if (now - parseInt(lastDismissed) < 86400000) return; // 24 hours
            }

            try {
                const active = await StorageService.getActivePromo();
                if (active) {
                    setPromo(active);
                    setIsVisible(true);
                }
            } catch (e) {
                console.warn("Promo engine offline.");
            }
        };
        checkPromo();
    }, [user?.planTier]);

    const handleDismiss = () => {
        localStorage.setItem('dismissed_promo_time', Date.now().toString());
        setIsVisible(false);
    };

    const handleCopy = () => {
        if (!promo?.couponCode) return;
        navigator.clipboard.writeText(promo.couponCode);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
    };

    if (!isVisible || !promo) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[120] lg:left-64 animate-slide-up px-3 pb-3 md:px-6 md:pb-6 pointer-events-none pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="max-w-5xl mx-auto pointer-events-auto">
                <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl md:rounded-[2.5rem] shadow-[0_-20px_50px_rgba(0,0,0,0.3)] p-3.5 md:p-5 flex flex-col md:flex-row items-center gap-3 md:gap-8 relative overflow-hidden group">
                    {/* Background Ambience */}
                    <div className="absolute top-0 right-0 w-64 h-full bg-gradient-to-l from-indigo-600/10 to-transparent pointer-events-none"></div>
                    
                    {/* Image / Icon Area */}
                    <div className="flex items-center gap-3 md:gap-4 w-full md:w-auto relative z-10">
                        {promo.imageUrl ? (
                            <div className="w-11 h-11 md:w-16 md:h-16 rounded-xl md:rounded-2xl overflow-hidden shadow-xl border-2 border-white/5 bg-slate-800 shrink-0">
                                <img src={promo.imageUrl} className="w-full h-full object-cover" alt="Promotion" />
                            </div>
                        ) : (
                            <div className="w-11 h-11 md:w-16 md:h-16 bg-indigo-600 rounded-xl md:rounded-2xl flex items-center justify-center shadow-xl shrink-0">
                                <Sparkles className="text-white" size={24} />
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 mb-0.5">
                                <Tag size={10} className="text-amber-400 shrink-0" />
                                <span className="text-[8px] md:text-[9px] font-black text-amber-400 tracking-widest uppercase truncate">{promo.title}</span>
                            </div>
                            <p className="text-white font-bold text-xs md:text-base leading-snug pr-8 md:pr-0 break-words">
                                {promo.message}
                            </p>
                        </div>
                        <button 
                            onClick={handleDismiss} 
                            className="md:hidden absolute top-0 right-0 min-w-[40px] min-h-[40px] flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                            aria-label="Dismiss promotion"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Actions Area */}
                    <div className="flex items-center gap-2 md:gap-4 w-full md:w-auto justify-end relative z-10 shrink-0">
                        {promo.couponCode && (
                            <button 
                                onClick={handleCopy}
                                className={`flex items-center justify-center gap-1.5 px-3.5 py-2.5 md:px-4 md:py-2.5 rounded-xl font-black text-[9px] md:text-[10px] tracking-widest transition-all min-h-[40px] ${isCopied ? 'bg-emerald-500 text-white' : 'bg-white/5 text-white hover:bg-white/10 border border-white/10'}`}
                            >
                                {isCopied ? <Check size={14}/> : <Copy size={14}/>}
                                <span>{isCopied ? 'Copied!' : promo.couponCode}</span>
                            </button>
                        )}
                        {promo.linkUrl && (
                            <a 
                                href={promo.linkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 md:flex-none px-5 py-2.5 md:px-8 md:py-3.5 bg-indigo-600 text-white rounded-xl md:rounded-2xl font-black text-[10px] md:text-xs tracking-widest shadow-xl hover:bg-indigo-500 transition-all active:scale-95 border-b-2 md:border-b-4 border-indigo-800 flex items-center justify-center gap-2 min-h-[40px]"
                            >
                                <Zap size={14} className="fill-current" /> 
                                <span>Claim Offer</span> 
                                <ExternalLink size={14} className="opacity-40" />
                            </a>
                        )}
                        <button 
                            onClick={handleDismiss} 
                            className="hidden md:flex p-3 bg-white/5 text-slate-500 hover:bg-rose-600 hover:text-white rounded-2xl transition-all group/close"
                            title="Close promotion"
                        >
                            <X size={18} className="group-hover/close:rotate-90 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
